import React, { useState, useEffect } from 'react';
import { ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface KiddingStat {
  doe_tag: string;
  kidding_date: string;
  kids_born: number;
  birth_type?: string;
  shed_id?: string;
  shed_name?: string;
}

interface DoeProductivity {
  doe_tag: string;
  total_kiddings: number;
  total_kids_born: number;
  average_kids_per_kidding: number;
  last_kidding_date?: string;
  days_since_last_kidding?: number;
  average_kidding_interval_days?: number;
  kids_per_year: number;
}

interface KiddingProductivitySummary {
  total_kiddings: number;
  total_kids_born: number;
  average_kids_per_kidding: number;
  single_births: number;
  double_births: number;
  triplet_births: number;
  quadruplet_plus_births: number;
  kid_survival_rate?: number;
  most_productive_does_count: number;
}

interface GoatKiddingProductivityReport {
  location_id: string;
  location_name: string;
  report_date: string;
  period_start: string;
  period_end: string;
  summary: KiddingProductivitySummary;
  kidding_stats: KiddingStat[];
  doe_productivity: DoeProductivity[];
  monthly_trends: Array<{ month: string; kiddings: number; kids_born: number }>;
}

const GoatKiddingProductivityReportPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<GoatKiddingProductivityReport | null>(null);

  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const januaryFirst = new Date(`${currentYear}-01-01T00:00:00+05:30`);
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(januaryFirst));
  }, []);

  useEffect(() => {
    if (selectedLocation?.id && startDate && endDate) {
      fetchReport();
    }
  }, [selectedLocation?.id, startDate, endDate]);

  const fetchReport = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/goat/report/kidding-productivity', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load kidding productivity report');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return <div className="container-mobile py-6 text-center">Loading...</div>;
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="text-center">
        <ChartBarIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Kidding & Productivity Report</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-gray-900">{report.summary.total_kiddings}</div>
              <div className="text-sm text-gray-600">Total Kiddings</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-green-600">{report.summary.total_kids_born}</div>
              <div className="text-sm text-gray-600">Total Kids Born</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-blue-600">{report.summary.average_kids_per_kidding.toFixed(2)}</div>
              <div className="text-sm text-gray-600">Avg Kids/Kidding</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-purple-600">{report.summary.most_productive_does_count}</div>
              <div className="text-sm text-gray-600">Highly Productive Does</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Birth Type Distribution</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{report.summary.single_births}</div>
                <div className="text-sm text-gray-600">Single</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{report.summary.double_births}</div>
                <div className="text-sm text-gray-600">Double</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{report.summary.triplet_births}</div>
                <div className="text-sm text-gray-600">Triplet</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{report.summary.quadruplet_plus_births}</div>
                <div className="text-sm text-gray-600">4+</div>
              </div>
            </div>
          </div>

          {report.doe_productivity.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Doe Productivity</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doe</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kiddings</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Kids</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Kids/Kidding</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kids/Year</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.doe_productivity.map((doe, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{doe.doe_tag}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.total_kiddings}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.total_kids_born}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.average_kids_per_kidding.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.kids_per_year.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.monthly_trends.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Monthly Trends</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kiddings</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kids Born</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.monthly_trends.map((month, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{month.month}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{month.kiddings}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{month.kids_born}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GoatKiddingProductivityReportPage;

